import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { LanguageService } from '@cutting/main/shared/services/language.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { LocalizationHelper, NgxLocalizedNumbersService } from 'cloud-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { NotificationService } from './main/shared/services/notification.service';

import { navigation } from './navigation/navigation';

@Component({
  selector: 'cutting-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  fuseConfig: any;
  navigation: any;

  // Private
  private unsubscribeAll: Subject<any> = new Subject();

  /**
   * Constructor
   * @param document
   * @param doc
   * @param locale
   * @param fuseConfigService
   * @param fuseNavigationService
   * @param fuseSidebarService
   * @param fuseSplashScreenService
   * @param platform
   * @param localizedNumbersService
   * @param el
   * @param iconRegistry
   * @param sanitizer
   * @param languageService
   * @param router
   * @param renderer
   * @param titleSetvice
   * @param notificationService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(LOCALE_ID) private locale: string,
    private fuseConfigService: FuseConfigService,
    private fuseNavigationService: FuseNavigationService,
    private fuseSidebarService: FuseSidebarService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private platform: Platform,
    private localizedNumbersService: NgxLocalizedNumbersService,
    public el: ElementRef,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private languageService: LanguageService,
    private router: Router,
    private renderer: Renderer2,
    private titleSetvice: Title,
    private notificationService: NotificationService
  ) {
    if (!environment.production) {
      this.renderer.setAttribute(this.doc.getElementById('favicon'), 'href', 'favicon-dev.ico');
      this.renderer.setAttribute(this.doc.getElementById('svg-favicon'), 'href', 'icon-dev.svg');
    }

    this.renderer.setAttribute(this.doc.documentElement, 'lang', this.locale);
    this.titleSetvice.setTitle(this.locale === 'ru' ? 'БАЗИС-Облако' : 'BAZIS-Cloud');

    this.iconRegistry.addSvgIconSet(this.sanitizer.bypassSecurityTrustResourceUrl('./assets/images/custom-icons.svg'));

    // Set LANG atribute for HTML root tag
    // this._translateService.onLangChange.pipe(
    //     map(x => x.lang))
    //     .subscribe(lang => this._document.documentElement.lang = lang);

    // Get default navigation
    this.navigation = navigation;

    // Register the navigation to the service
    this.fuseNavigationService.register('main', this.navigation);

    // Set the main navigation as our current navigation
    this.fuseNavigationService.setCurrentNavigation('main');

    // Set language by user
    this.setLanguageByUser();

    const client = LocalStorageManager.client;

    if (client) {
      const localizedCode = LocalizationHelper.getLocalizedNumberCodeByISOCode(client.currencyCode);
      this.localizedNumbersService.setLocale(localizedCode);
    } else {
      this.localizedNumbersService.setLocale('ru_NRU');
    }

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     */

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.
    /**
     * setTimeout(() => {
     *    this._translateService.setDefaultLang('en');
     *    this._translateService.setDefaultLang('tr');
     * });
     */

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix End
     * ----------------------------------------------------------------------------------------------------
     */

    // Add is-mobile class to the body if the platform is mobile
    if (this.platform.ANDROID || this.platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to config changes
    this.fuseConfigService.config.pipe(takeUntil(this.unsubscribeAll)).subscribe((config: any) => {
      this.fuseConfig = config;

      // Boxed
      if (this.fuseConfig.layout.width === 'boxed') {
        this.document.body.classList.add('boxed');
      } else {
        this.document.body.classList.remove('boxed');
      }

      // Color theme - Use normal for loop for IE11 compatibility
      for (const className of this.document.body.classList) {
        if (className.startsWith('theme-')) {
          this.document.body.classList.remove(className);
        }
      }

      this.document.body.classList.add(this.fuseConfig.colorTheme);
    });

    this.notificationService.connect(/*user.token.access*/);
  }

  getCurrentRoute(): string {
    return this.router.url;
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   * @param key
   */
  toggleSidebarOpen(key: any): void {
    this.fuseSidebarService.getSidebar(key).toggleOpen();
  }

  private setLanguageByUser(): void {
    const client = LocalStorageManager.client;

    if (!client) {
      return;
    }

    const currentLang = LocalStorageManager.currentLang;

    if (currentLang && this.locale !== currentLang) {
      window.location.href = window.location.href =
        window.location.origin + '/cutting/' + currentLang + '/#' + this.getCurrentRoute();
    } else if (!currentLang) {
      if (client) {
        this.languageService
          .changeLanguage(this.locale)
          .pipe(takeUntil(this.unsubscribeAll))
          .subscribe(() => {
            this.saveLangToLS(this.locale);
          });
      } else {
        LocalStorageManager.currentLang = this.locale;
      }
    }
  }

  private saveLangToLS(lang: string): void {
    const client = LocalStorageManager.client;
    client.languageCode = lang;
    LocalStorageManager.client = client;
  }
}
